import { useEffect, useMemo, useRef, useState } from 'react'

import { IconFilter, IconSearch } from '@tabler/icons-react'
import { Skeleton, Tooltip } from 'antd'
import { diff } from 'deep-object-diff'
import { capitalize } from 'lodash'
import styled, { css } from 'styled-components'

import { tryGetHumanReadableFilters } from './CorePageListViewEmpty'
import { Wrapper } from './CorePageListViewFilterBar'
import { CorePageListViewFilterModal } from './CorePageListViewFilterModal'
import { CorePageListViewSort, SortOption } from './CorePageListViewSort'

import { FilterObjectType, useFilters } from '@/context/filterContext'
import { useQueryParams } from '@/context/queryParamsContext'
import { useTimezonedDates } from '@/context/timezoneContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import useHighlight from '@/hooks/useHighlight'
import { Params } from '@/utils/params'

const BarWrapper = styled(Wrapper)`
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
`

const FilterIconWrapper = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SearchText = styled.span`
  font-weight: 400;
`

const FilterCount = styled.span`
  ${({ theme }) => theme.font.caption01}
  font-size: 0.625rem;
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colour.secondary._50};
  color: ${({ theme }) => theme.colour.secondary._600};
  display: flex;
  align-items: center;
  justify-content: center;
`

type ViewButtonProps = {
  active: boolean
  variant?: View['nonZeroVariant']
}

const ViewButton = styled.button<ViewButtonProps>`
  flex-shrink: 0;
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
  position: relative;
  padding: 0.625rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colour.secondary._500};
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${({ variant, theme }) =>
    variant === 'danger' &&
    css`
      color: ${theme.colour.semantic.errorDark};
    `}

  ${({ variant, theme }) =>
    variant === 'warning' &&
    css`
      color: ${theme.colour.semantic.warningDark};
    `}

  ${({ active, variant, theme }) =>
    active &&
    !variant &&
    css`
      color: ${theme.colour.primary._900};
    `}
`

const FilterButton = styled(ViewButton).attrs({ active: false })`
  color: ${({ theme }) => theme.colour.secondary.base};
  border: 1px solid ${({ theme }) => theme.colour.secondary._100};

  &:hover {
    background-color: ${({ theme }) => theme.colour.neutral.surface};
  }
`

type HighlightProps = {
  variant?: ViewButtonProps['variant']
}

const Highlight = styled.div<HighlightProps>`
  position: absolute;
  background-color: ${({ theme }) => theme.colour.primary._50};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: 0.2s;

  ${({ variant, theme }) =>
    variant === 'danger' &&
    css`
      background-color: ${theme.colour.semantic.errorLight};
    `}

  ${({ variant, theme }) =>
    variant === 'warning' &&
    css`
      background-color: ${theme.colour.semantic.warningLight};
    `}
`

const ViewsWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  margin-left: 1.5rem;
  display: flex;
  gap: 0.5rem;
  overflow: auto;
`

const SectionDivider = styled.div`
  align-self: stretch;
  width: 2px;
  background-color: ${({ theme }) => theme.colour.secondary._50};
  margin: 0.5rem 1.125rem;
`

type FilterValue =
  FilterObjectType['filters'][keyof FilterObjectType['filters']]

/**
 * Covers the case where you want to have an "ALL" view which is definied (for example)
 * as an empty string or empty array. This function checks if the viewFilterValue is
 * empty and if so, checks if the filterValue is also empty. We assume a missing value
 * is the same as an empty value.
 */
const checkForEmpty = (
  viewFilterValue: FilterValue,
  filterValue: FilterValue
) => {
  if (Array.isArray(viewFilterValue) && viewFilterValue.length === 0) {
    const filterValueAsArray = filterValue as Array<unknown>

    if (!filterValueAsArray) return true // assume missing value is the same as empty array

    return filterValueAsArray.length === 0
  }

  if (typeof viewFilterValue === 'string' && viewFilterValue === '') {
    const filterValueAsString = filterValue as string

    if (!filterValueAsString) return true // assume missing value is the same as empty string

    return filterValueAsString === ''
  }
}

const paginationAndSortKeys: string[] = [
  'page',
  'pageSize',
  'direction',
  'sortKey',
] as Array<keyof Params.Pagination | keyof Params.Sort>

const excludePaginationAndSort = (params: Record<string, unknown>) => {
  const keys = Object.keys(params).filter(
    (key) => !paginationAndSortKeys.includes(key)
  )

  return keys.reduce((acc, key) => {
    return { ...acc, [key]: params[key] }
  }, {})
}

const getActiveView = (
  filters: FilterObjectType['filters'],
  views: Props['views']
) => {
  const view = views.find((view) => {
    const filterKeys = Object.keys(view.filters)

    const sanitisedKeys = Object.keys(filters ?? {}).filter(
      (key) => !paginationAndSortKeys.includes(key)
    )

    if (filterKeys.length !== sanitisedKeys.length) {
      return false // ensures the filters exactly match
    }

    return filterKeys.every((key) => {
      if (checkForEmpty(view.filters[key], filters?.[key])) {
        return true
      }

      const viewObj = { [key]: view.filters[key] }
      const filterObj = { [key]: filters?.[key] }

      const keys = diff(viewObj, filterObj)
      return Object.keys(keys).length === 0
    })
  })

  return view
}

const getViewButtonVariant = (view: View): ViewButtonProps['variant'] => {
  if (!view?.count) return view?.fallbackVariant ?? undefined

  if (view.nonZeroVariant) {
    if (view.count > 0) return view.nonZeroVariant
  }

  if (view.zeroVariant) {
    if (view.count === 0) return view.zeroVariant
  }

  return undefined
}

export type View = {
  label: string
  count?: number
  filters: FilterObjectType['filters']
  fallbackVariant?: 'danger' | 'warning'
  nonZeroVariant?: 'danger' | 'warning'
  zeroVariant?: 'danger' | 'warning'
}

type ViewTagProps = {
  view: View
  active: boolean
  useQuery?: boolean
}

const ViewTag: React.FC<ViewTagProps> = ({ view, active, useQuery }) => {
  const storedCount = useRef(view.count)

  const { setParams } = useQueryParams()
  const { routeFilters, updateFilters } = useFilters()

  const onClick = () => {
    if (useQuery) {
      setParams(view.filters)
    } else {
      updateFilters({ ...routeFilters, filters: view.filters })
    }
  }

  const count = useMemo(() => {
    if (isNaN(view.count) && isNaN(storedCount.current)) return undefined // don't show anything if we don't have a count

    if (isNaN(view.count) && !isNaN(storedCount.current))
      return storedCount.current // when in-between queries, used stored count

    storedCount.current = view.count // update stored count for next time
    return view.count
  }, [view.count])

  return (
    <ViewButton
      active={active}
      onClick={onClick}
      variant={getViewButtonVariant(view)}
      className={`highlight-${view.label}`}
    >
      {view.label}
      {!isNaN(count) && <span>{count}</span>}
    </ViewButton>
  )
}

type Props = {
  views: View[]
  loading?: boolean
  searchable?: boolean
  searchFilterKey?: string
  filters?: Array<React.ReactNode | React.ReactNode[]>
  sortOptions?: SortOption[]
  useQuery?: boolean
}

export const CorePageListViewViewsFilterBar: React.FC<Props> = ({
  views,
  loading,
  sortOptions,
  searchable,
  searchFilterKey,
  filters,
  useQuery,
}) => {
  const isMobile = useBreakpoint('sm')

  const { formatDateString, formatDateRangeString } = useTimezonedDates()
  const { routeFilters } = useFilters()

  const { params } = useQueryParams()

  const [filterModalOpen, setFilterModalOpen] = useState(false)

  const activeView = useMemo(() => {
    return getActiveView(
      useQuery ? excludePaginationAndSort(params) : routeFilters?.filters,
      views
    )
  }, [routeFilters?.filters, views, useQuery, params])

  const { highlightPos, recalculate } = useHighlight(activeView?.label)

  const searchText = useMemo<string>(() => {
    if (useQuery) {
      return params?.[searchFilterKey] as string
    }

    return routeFilters?.filters?.[searchFilterKey]?.[0] as string
  }, [routeFilters?.filters, searchFilterKey, params, useQuery])

  const activeFilters = useMemo(() => {
    const filters = tryGetHumanReadableFilters(
      useQuery ? excludePaginationAndSort(params) : routeFilters?.filters,
      {
        date: formatDateString,
        dateRange: formatDateRangeString,
      }
    )
    return filters.filter((f) => f.key !== searchFilterKey)
  }, [
    routeFilters?.filters,
    searchFilterKey,
    formatDateString,
    formatDateRangeString,
    params,
    useQuery,
  ])

  const countCount = useMemo(() => {
    return views.reduce((acc, v) => acc + (v.count ?? 0), 0)
  }, [views])

  useEffect(() => {
    recalculate()
  }, [countCount, recalculate])

  if (loading) {
    return (
      <Wrapper>
        <Skeleton.Input
          active
          size="small"
          style={{ width: '88px', minWidth: '88px' }}
        />

        <ViewsWrapper>
          {views.map((view) => (
            <Skeleton.Input
              key={view.label.toString()}
              active
              size="small"
              style={{ width: '88px', minWidth: '88px' }}
            />
          ))}
        </ViewsWrapper>

        <SectionDivider />

        <Skeleton.Input
          active
          size="small"
          style={{ width: '48px', minWidth: '48px' }}
        />
      </Wrapper>
    )
  }

  return (
    <>
      <BarWrapper>
        <FilterButton onClick={() => setFilterModalOpen(true)}>
          {searchable && <IconSearch size={14} />}

          {!!searchText?.length && <SearchText>{searchText}</SearchText>}

          <FilterIconWrapper>
            <IconFilter size={16} />
            {!!activeFilters?.length && (
              <Tooltip
                title={activeFilters
                  .map((f) => `${capitalize(f.key)} - ${f.value}`)
                  .join(', ')}
              >
                <FilterCount>{activeFilters?.length}</FilterCount>
              </Tooltip>
            )}
          </FilterIconWrapper>
        </FilterButton>

        <ViewsWrapper>
          {highlightPos && (
            <Highlight
              variant={getViewButtonVariant(activeView)}
              style={{
                width: highlightPos.width + 'px',
                height: highlightPos.height + 'px',
                top: highlightPos.offsetY + 'px',
                left: highlightPos.offsetX + 'px',
              }}
            />
          )}

          {views.map((view) => (
            <ViewTag
              key={view.label.toString()}
              view={view}
              active={activeView?.label === view.label}
              useQuery={useQuery}
            />
          ))}
        </ViewsWrapper>

        <SectionDivider />

        <CorePageListViewSort
          compact
          isMobile={isMobile}
          sortOptions={sortOptions}
          useQuery={useQuery}
        />
      </BarWrapper>

      <CorePageListViewFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        filters={filters}
        showSearch={searchable}
        searchFilterKey={searchFilterKey}
        useQuery={useQuery}
      />
    </>
  )
}
